/**
 * Local lockbox state — AsyncStorage only. No sync, no server, no audit pipeline.
 */

import AsyncStorage from "@react-native-async-storage/async-storage";
import { evaluateLockbox } from "../domain/evaluate";
import { DEFAULT_RULEPACK } from "../domain/rulepackDefaults";
import { normalizeBundleId } from "../domain/restrictionTargets";
import { defaultClassification } from "../domain/types.js";
import { submitRestrictionIntent } from "../adapters/enforcementAdapter";
import { classifyApp, classifyAppHeuristicOnly } from "../classification/classifyApp.js";
import { notifySeverityAutoBlock } from "../utils/severityAutoBlock.js";

const K_RULEPACK = "lockbox_rulepack_v1";
const K_INVENTORY = "lockbox_inventory_v1";
const K_MODE = "lockbox_active_mode_v1";
const K_EMERGENCY = "lockbox_emergency_v1";
const K_ENFORCEMENT_MODE = "lockbox_enforcement_mode_v1";

/** @typedef {import('../domain/types.js').LockboxRulepack} LockboxRulepack */
/** @typedef {import('../domain/types.js').LockboxAppEntity} LockboxAppEntity */
/** @typedef {import('../domain/types.js').EnforcementPlan} EnforcementPlan */
/** @typedef {import('../domain/types.js').AppClassification} AppClassification */

function newId() {
  return `lb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/** @type {LockboxRulepack} */
let rulepack = { ...DEFAULT_RULEPACK };

/** @type {LockboxAppEntity[]} */
let inventory = [];

/** @type {string} */
let activeModeId = "normal";

/** @type {{ active: boolean, until: Date | null }} */
let emergency = { active: false, until: null };

/** @type {EnforcementPlan | null} */
let lastPlan = null;

/** @type {'monitor'|'soft'|'strict'} */
let nativeEnforcementMode = "monitor";

let planRevision = 0;

const listeners = new Set();

/**
 * Build a fresh snapshot object. Mutate only via `refreshCachedSnapshot` so `useSyncExternalStore` sees a
 * stable reference between store updates (React compares snapshots with `Object.is`).
 */
function buildSnapshot() {
  return {
    rulepack: JSON.parse(JSON.stringify(rulepack)),
    inventory: JSON.parse(JSON.stringify(inventory)),
    activeModeId,
    emergency: {
      active: emergency.active,
      until: emergency.until,
    },
    lastPlan: lastPlan ? JSON.parse(JSON.stringify(lastPlan)) : null,
    nativeEnforcementMode,
  };
}

/** @type {ReturnType<typeof buildSnapshot>} */
let cachedSnapshot = buildSnapshot();

function refreshCachedSnapshot() {
  cachedSnapshot = buildSnapshot();
}

function emit() {
  refreshCachedSnapshot();
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* ignore subscriber errors */
    }
  }
}

/**
 * @returns {{ rulepack: LockboxRulepack, inventory: LockboxAppEntity[], activeModeId: string, emergency: { active: boolean, until: Date | null }, lastPlan: EnforcementPlan | null }}
 * `lastPlan` is the latest **restriction intent** (same shape as EnforcementPlan); Apple/OS enforces when bridged.
 */
function getSnapshot() {
  return cachedSnapshot;
}

async function persist() {
  await AsyncStorage.multiSet([
    [K_RULEPACK, JSON.stringify(rulepack)],
    [K_INVENTORY, JSON.stringify(inventory)],
    [K_MODE, activeModeId],
    [K_EMERGENCY, JSON.stringify({ active: emergency.active, untilIso: emergency.until ? emergency.until.toISOString() : null })],
  ]);
}

function recompute() {
  const now = new Date();
  let until = emergency.until;
  if (until && until <= now) {
    emergency = { active: false, until: null };
  }
  const world = {
    now,
    activeModeId,
    emergency,
    apps: inventory,
  };
  planRevision += 1;
  const evaluated = evaluateLockbox(rulepack, world);
  /** @type {Record<string, string>} */
  const androidBlockReasons = {};
  for (const app of inventory) {
    const pkg = app.packageName || app.bundleId;
    if (!pkg || !evaluated.blockAppIds.includes(app.id)) continue;
    const match = evaluated.matchedRules.find((r) => r.appId === app.id);
    if (match?.reason) androidBlockReasons[pkg] = match.reason;
  }
  lastPlan = {
    ...evaluated,
    planId: evaluated.planId || `plan_${planRevision}`,
    revision: planRevision,
    enforcementMode: nativeEnforcementMode,
    androidBlockReasons,
  };
  emit();
  void persist();
  void submitRestrictionIntent(lastPlan).catch(() => {});
  return lastPlan;
}

/**
 * Load from device storage. Call once at app startup.
 */
export async function hydrateLockboxStore() {
  try {
    const entries = await AsyncStorage.multiGet([K_RULEPACK, K_INVENTORY, K_MODE, K_EMERGENCY]);
    const map = Object.fromEntries(entries);
    if (map[K_RULEPACK]) {
      const p = JSON.parse(map[K_RULEPACK]);
      if (p && typeof p.version === "number") rulepack = p;
    }
    if (map[K_INVENTORY]) {
      const inv = JSON.parse(map[K_INVENTORY]);
      if (Array.isArray(inv)) inventory = inv;
    }
    if (map[K_MODE]) {
      activeModeId = String(map[K_MODE]);
    }
    if (map[K_EMERGENCY]) {
      const e = JSON.parse(map[K_EMERGENCY]);
      emergency = {
        active: !!e.active,
        until: e.untilIso ? new Date(e.untilIso) : null,
      };
    }
    if (map[K_ENFORCEMENT_MODE]) {
      const m = String(map[K_ENFORCEMENT_MODE]);
      if (m === "monitor" || m === "soft" || m === "strict") nativeEnforcementMode = m;
    }
  } catch {
    /* keep defaults */
  }
  recompute();
}

export function subscribeLockbox(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getLockboxState() {
  return getSnapshot();
}

/** @param {LockboxRulepack} next */
export function setRulepack(next) {
  rulepack = { ...next, version: next.version ?? 1 };
  recompute();
}

/**
 * @param {string} modeId
 * @param {import('../domain/types.js').BehavioralCategory} category
 */
export function isCategoryBlockedInMode(modeId, category) {
  const rule = rulepack.modeStrict.find((m) => m.modeId === modeId);
  return !!rule?.extraBlockCategories?.includes(category);
}

/**
 * Toggle whether the active mode blocks a behavioral category (App Library "Block all in category").
 * @param {import('../domain/types.js').BehavioralCategory} category
 * @param {boolean} blocked
 */
export function setCategoryBlockedInActiveMode(category, blocked) {
  const modeId = activeModeId;
  const rp = JSON.parse(JSON.stringify(rulepack));
  let rule = rp.modeStrict.find((/** @type {{ modeId: string }} */ m) => m.modeId === modeId);
  if (!rule) {
    rule = { modeId, extraBlockCategories: [] };
    rp.modeStrict.push(rule);
  }
  const set = new Set(rule.extraBlockCategories || []);
  if (blocked) set.add(category);
  else set.delete(category);
  rule.extraBlockCategories = Array.from(set).sort();
  setRulepack(rp);
}

/**
 * @param {import('../domain/types.js').BehavioralCategory[]} categories
 * @param {boolean} blocked
 */
export function setGroupCategoriesBlockedInActiveMode(categories, blocked) {
  for (const cat of categories) {
    setCategoryBlockedInActiveMode(cat, blocked);
  }
}

/** Bulk-enable mode enforcement for all genres in this mode's blockedCategories template. */
export function applyActiveModeBlockAll() {
  const cats = getModeBlockedCategories(activeModeId, rulepack);
  setGroupCategoriesBlockedInActiveMode(cats, true);
}

/** @param {string} modeId */
export function setActiveMode(modeId) {
  activeModeId = String(modeId || "normal");
  recompute();
}

/** @param {boolean} active @param {Date|null} until */
export function setEmergency(active, until = null) {
  emergency = { active: !!active, until: until || null };
  recompute();
}

/**
 * @param {Omit<LockboxAppEntity, 'id'> & { id?: string }} partial
 * @returns {LockboxAppEntity}
 */
export function upsertApp(partial) {
  const id = partial.id || newId();
  const idx = inventory.findIndex((a) => a.id === id);
  const existing = idx >= 0 ? inventory[idx] : null;
  const pkgRaw = partial.packageName != null ? String(partial.packageName).trim() : "";
  const displayName = partial.displayName ?? existing?.displayName ?? "App";

  let classification = partial.classification;
  if (!classification) {
    const quick = classifyAppHeuristicOnly(pkgRaw || partial.bundleId || "", displayName);
    classification = quick.classification;
  }

  const next = /** @type {LockboxAppEntity} */ ({
    id,
    displayName,
    classification,
    nativeRef: partial.nativeRef !== undefined ? partial.nativeRef : existing?.nativeRef ?? null,
  });
  const b = partial.bundleId != null ? String(partial.bundleId).trim() : "";
  if (b) {
    next.bundleId = normalizeBundleId(b);
  } else if (existing?.bundleId) {
    next.bundleId = existing.bundleId;
  }
  const pkg = pkgRaw || (partial.bundleId != null ? String(partial.bundleId).trim() : "");
  if (pkg) {
    next.packageName = normalizePackageName(pkg);
  } else if (existing?.packageName) {
    next.packageName = existing.packageName;
  } else if (next.bundleId) {
    next.packageName = next.bundleId;
  }
  if (idx >= 0) inventory[idx] = next;
  else inventory.push(next);
  recompute();
  return next;
}

/**
 * Add/update app with Layer 1 instantly, then Layer 2 (Play + optional Claude) in background.
 * @param {Omit<LockboxAppEntity, 'id'> & { id?: string, runDeepClassification?: boolean }} partial
 * @returns {Promise<LockboxAppEntity>}
 */
export async function classifyAndUpsertApp(partial) {
  const row = upsertApp({ ...partial, classification: partial.classification });
  if (partial.runDeepClassification === false) {
    return row;
  }
  if (partial.runDeepClassification === true || !partial.classification) {
    await runDeepClassification(row.id);
  }
  return getSnapshot().inventory.find((a) => a.id === row.id) || row;
}

/**
 * Re-run Play Store + AI classification for an inventory row.
 * @param {string} appId
 */
export async function runDeepClassification(appId) {
  const app = inventory.find((a) => a.id === appId);
  if (!app) return;
  const result = await classifyApp({
    displayName: app.displayName,
    packageName: app.packageName || app.bundleId,
    bundleId: app.bundleId,
  });
  patchAppClassification(appId, result.classification);
  maybeApplySeverityAutoBlock(appId);
}

/**
 * Global block when contentSeverity is flagged (all modes). Skips if user force-allowed.
 * @param {string} appId
 */
export function maybeApplySeverityAutoBlock(appId) {
  const app = inventory.find((a) => a.id === appId);
  if (!app) return;
  const c = app.classification;
  if (c.override?.kind === "force_allow") return;
  if (c.contentSeverity !== "flagged") return;
  if (c.severityAutoBlocked) return;

  patchAppClassification(appId, {
    severityAutoBlocked: true,
    override: {
      kind: "force_block",
      reason: "Auto-blocked: disturbing or explicit content detected",
    },
  });
  void notifySeverityAutoBlock(app.displayName);
}

/**
 * User tapped "Block — contains disturbing content".
 * @param {string} appId
 */
export function setUserDisturbingBlock(appId) {
  const app = inventory.find((a) => a.id === appId);
  if (!app) return;
  patchAppClassification(appId, {
    userFlaggedSeverity: true,
    severityAutoBlocked: false,
    override: {
      kind: "force_block",
      reason: "User blocked — disturbing content",
    },
  });
}

/** @param {string} appId */
export function clearUserDisturbingBlock(appId) {
  const app = inventory.find((a) => a.id === appId);
  if (!app) return;
  const keepAuto =
    app.classification.contentSeverity === "flagged" && app.classification.severityAutoBlocked;
  patchAppClassification(appId, {
    userFlaggedSeverity: false,
    override: keepAuto
      ? { kind: "force_block", reason: "Auto-blocked: disturbing or explicit content detected" }
      : { kind: "none" },
  });
}

/** @param {string} appId */
export function setAppForceAllow(appId) {
  patchAppClassification(appId, {
    userFlaggedSeverity: false,
    severityAutoBlocked: false,
    override: { kind: "force_allow", reason: "User allowed" },
  });
}

/** @param {import('../domain/types.js').BehavioralCategory} category */
export function setAppCategory(appId, category) {
  const app = inventory.find((a) => a.id === appId);
  if (!app) return;
  patchAppClassification(appId, {
    primaryCategory: category,
  });
}

/** @param {string} id */
export function removeApp(id) {
  inventory = inventory.filter((a) => a.id !== id);
  recompute();
}

/** @param {string} id @param {Partial<AppClassification>} patch */
export function patchAppClassification(id, patch) {
  const app = inventory.find((a) => a.id === id);
  if (!app) return;
  app.classification = {
    ...app.classification,
    ...patch,
    scores: patch.scores ? { ...app.classification.scores, ...patch.scores } : app.classification.scores,
    override: patch.override ? { ...app.classification.override, ...patch.override } : app.classification.override,
    classificationMeta: patch.classificationMeta
      ? { ...app.classification.classificationMeta, ...patch.classificationMeta }
      : app.classification.classificationMeta,
  };
  recompute();
}

/**
 * @param {string} id
 * @param {{ bundleId?: string|null, nativeRef?: import('../domain/types.js').NativeAppRef|null }} patch
 */
export function patchAppIdentity(id, patch) {
  const app = inventory.find((a) => a.id === id);
  if (!app) return;
  if (patch.bundleId !== undefined) {
    const b = String(patch.bundleId || "").trim();
    if (b) app.bundleId = normalizeBundleId(b);
    else delete app.bundleId;
  }
  if (patch.nativeRef !== undefined) {
    app.nativeRef = patch.nativeRef;
  }
  recompute();
}

/** @param {'monitor'|'soft'|'strict'} mode */
export function setNativeEnforcementMode(mode) {
  if (mode !== "monitor" && mode !== "soft" && mode !== "strict") return;
  nativeEnforcementMode = mode;
  recompute();
}

export function resetLockboxToDefaults() {
  rulepack = { ...DEFAULT_RULEPACK };
  inventory = [];
  activeModeId = "normal";
  emergency = { active: false, until: null };
  nativeEnforcementMode = "monitor";
  recompute();
}

export const LOCKBOX_MODES = [
  { id: "normal", label: "Normal" },
  { id: "work", label: "Work" },
  { id: "sleep", label: "Sleep" },
];
